import React, { useEffect, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../../../store/store'
import { updateUser } from '../../../../../../../../../store/reducers/userReducer'
import { InputsWrapper } from '../../../../style'
import { useTranslation } from 'react-i18next'
import { UserExistsError } from './style'
import {
  setAuthFormTexts,
  setForm,
  setIsLayoutLoading,
} from 'store/reducers/layoutReducer'
import { IdentityProviderInput } from 'shared/components/identity-provider-input/identityProviderInput'
import { IdentityProvider } from 'types/types/IdentityProvider'
import { FormTypes } from 'types/enums/EFormTypes'
import { EPersonalDetailsFormSteps } from 'types/enums/EPersonalDetailsFormSteps'
import { Support } from 'shared/components/support/Support'
import { Button } from 'shared/components/button/Button'
import { useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { ISFUser } from 'types/interfaces/ISFUser'
import { useCreateSfUserMutation } from 'store/api-slices/userSlice'
import { IUser } from 'types/interfaces/IUser'

type IdentityProviderFormData = {
  id: string
  identityProvider: IdentityProvider
}
type IdentityProviderFormErrors = Partial<
  Record<keyof IdentityProviderFormData, boolean>
>

export const PersonalDetailsIdentityProvider: React.FC = () => {
  const user = useAppSelector<IUser>((state) => state.user)
  const layoutEmail = useAppSelector<string>((state) => state.layout.user.email)
  const userId = useAppSelector((state) => state.user.personalDetails?.id)
  const isUserExits = useAppSelector((state) => state.user.isUserExits)
  const dispatch = useAppDispatch()
  const [signupFormIdentityProviderData, setSignupFormIdentityProviderData] =
    useState<IdentityProviderFormData>({
      id: userId || '',
      identityProvider: IdentityProvider.ID,
    })
  const [errors, setErrors] = useState<IdentityProviderFormErrors>({
    id: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)

  const [createSfUser] = useCreateSfUserMutation()

  const { t } = useTranslation('', {
    keyPrefix: `pages.signUp.identityProvider`,
  })

  useEffect(() => {
    dispatch(
      setAuthFormTexts({
        title: t(`${signupFormIdentityProviderData.identityProvider}.title`),
        subtitle: t(
          `${signupFormIdentityProviderData.identityProvider}.subtitle`,
        ),
      }),
    )
  }, [signupFormIdentityProviderData.identityProvider, t])

  useEffect(() => {
    if (signupFormIdentityProviderData.id && !errors.id && !isUserExits) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [signupFormIdentityProviderData, isUserExits])

  const handleIdentityProviderChange = (
    value: IdentityProvider,
    error?: boolean,
  ) => {
    dispatch(updateUser({ isUserExits: false }))
    setErrors({ ...errors, id: error })
    setSignupFormIdentityProviderData({
      ...signupFormIdentityProviderData,
      identityProvider: value,
    })
  }

  const handleFieldChange = <
    T extends keyof typeof signupFormIdentityProviderData,
  >(
    field: T,
    value?: (typeof signupFormIdentityProviderData)[T],
    error?: boolean,
  ) => {
    if (userId !== value) {
      dispatch(updateUser({ isUserExits: false }))
    }
    setErrors({ ...errors, [field]: error })
    setSignupFormIdentityProviderData({
      ...signupFormIdentityProviderData,
      [field]: value,
    })
  }

  const updateUserDetails = (sfUser: ISFUser) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { email, ...sfUserData } = sfUser
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { addresses, ...personalDetails } = sfUser.personalDetails || {}
    dispatch(
      updateUser({
        ...sfUserData,
        personalDetails,
        isRegistered: true,
      }),
    )
  }

  const nextButtonClicked = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    dispatch(setIsLayoutLoading(true))
    dispatch(
      updateUser({
        personalDetails: {
          id: signupFormIdentityProviderData.id,
          identityProvider: signupFormIdentityProviderData.identityProvider,
        },
      }),
    )
    try {
      const sfUser = await createSfUser({
        email: user.email || layoutEmail,
        terms: user.terms,
        newsletter: user.newsletter,
        ...user.personalDetails,
        id: signupFormIdentityProviderData.id,
        identityProvider: signupFormIdentityProviderData.identityProvider,
      }).unwrap()

      if (sfUser) {
        const { success, res } = sfUser
        if (success) {
          updateUserDetails(res)
          dispatch(
            setForm({
              personalDetailsStep: EPersonalDetailsFormSteps.extraPersonalInfo,
            }),
          )
        } else {
          dispatch(setIsLayoutLoading(false))
          dispatch(updateUser({ isUserExits: true }))
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(updateUser({ isUserExits: true }))
      dispatch(setIsLayoutLoading(false))
    }
  }

  const navigate = useNavigate()

  return (
    <>
      <InputsWrapper>
        <IdentityProviderInput
          value={signupFormIdentityProviderData.id}
          identityValue={signupFormIdentityProviderData.identityProvider}
          callback={(value, error) => handleFieldChange('id', value, error)}
          identityCallback={handleIdentityProviderChange}
          form={FormTypes.SIGN_UP_FORM}
          hasError={errors.id}
        />
      </InputsWrapper>
      <UserExistsError className={isUserExits ? 'error' : ''}>
        {t('buttons.next.errorText')}
      </UserExistsError>
      <Support />
      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.next.text')}
        callback={async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          await nextButtonClicked(e)
        }
      />
      <Button
        type="reset"
        disabled={false}
        text={isUserExits ? t('buttons.login.text') : t('buttons.back.text')}
        callback={() =>
          isUserExits
            ? navigate(EAppRoutes.LOGIN)
            : dispatch(
                setForm({
                  personalDetailsStep: EPersonalDetailsFormSteps.personalInfo,
                }),
              )
        }
      />
    </>
  )
}
